import { useChangeCase } from "@vueuse/integrations/useChangeCase";
import {
  getContacts,
  getTemplates,
  getUnreadCounts,
  getCoreContacts,
} from "src/api/chat";

import TemplateForm from "src/modules/chat/components/TemplateForm.vue";
import TemplatesDrawer from "src/modules/chat/components/TemplatesDrawer.vue";

const getInitialState = () => {
  return {
    chatRoomSearchQuery: "",

    contacts: [],
    coreContacts: [],
    contactConversations: [],

    templates: [],

    unreadCounts: {},
  };
};

export const useChatStore = defineStore("chat", () => {
  let state = reactive(getInitialState());

  const {
    loadingConversations,

    conversationsClient,
    conversationsState,
    connectionState,

    conversations,

    unreadConversationsCount,
    cachedUnreadResults,

    getConversationBySid,
    getConversationUnreadCount,
    conversationHasUnreadMessages,
    calculateUnreadCount,
    executeCalculateUnreadCount,
  } = useConversations();

  const { state: templatesDrawerState, toggle: toggleTemplatesDrawer } =
    useDrawer({
      component: TemplatesDrawer,
    });

  const { state: templateFormDrawerState, toggle: toggleTemplateFormDrawer } =
    useDrawer({
      component: TemplateForm,
    });

  const detachMatchedMessages = () => {
    for (const cc of state.contactConversations) {
      const conversation = unref(conversations).poshn.find(
        (c) => c.sid === cc.conversationSid,
      );

      if (conversation) {
        delete conversation.matchedMessage;
      }
    }
  };

  const setContactConversations = (contactConversations = []) => {
    state.contacts = contactConversations.filter(
      (contact) => !contact.conversationSid,
    );

    state.contactConversations = contactConversations.filter(
      (contact) => contact.conversationSid,
    );
  };

  const setCoreContacts = (coreContacts) => {
    state.coreContacts = coreContacts;
  };

  const { normalizePhone } = useChatUtils();

  /**
   * Filter out nucleus contacts that have matching phone and shop_name in chat contacts
   * Matches coreContacts phone+shop_name against conversationContacts phone+businessName
   * Only includes business name/shop name in identifier if contact has role === 'agent'
   */
  const filterOutDuplicateCoreContacts = ({
    coreContacts,
    conversationContacts,
  }) => {
    // Create a Set of normalized identifiers from conversation contacts
    // using phone and businessName combination (only if role is 'agent')
    const conversationIdentifiers = new Set(
      conversationContacts
        .map((contact) => {
          const normalizedPhone = normalizePhone(contact.phone, {
            includeIsdCode: false,
          });

          if (!normalizedPhone && !contact.businessName) {
            return null;
          }

          // Only include businessName in identifier if role is 'agent'
          const paramCaseBusinessName =
            contact.role === "agent"
              ? useChangeCase(
                  contact?.businessName?.toLowerCase() || "",
                  "paramCase",
                ).value
              : "";

          return `${normalizedPhone}${paramCaseBusinessName}`;
        })
        .filter(Boolean),
    );

    // Filter out nucleus contacts with matching phone and shop_name combination
    return coreContacts.filter((coreContact) => {
      const normalizedPhone = normalizePhone(coreContact.phone, {
        includeIsdCode: false,
      });

      if (!normalizedPhone && !coreContact.shop_name) {
        return true; // Keep if both are missing
      }

      // Only include shop_name in identifier if role is 'agent'
      const paramCaseShopName =
        coreContact.role === "agent"
          ? useChangeCase(
              coreContact?.shop_name?.toLowerCase() || "",
              "paramCase",
            ).value
          : "";

      const coreContactIdentifier = `${normalizedPhone}${paramCaseShopName}`;

      return !conversationIdentifiers.has(coreContactIdentifier);
    });
  };

  const { filter, pagination, params } = usePagination();

  const getContactConversations = async (options = {}) => {
    options = unref(options);

    options.params = {
      ...unref(params),
      ...options.params,
    };

    const [conversationResponse, coreResponse] = await Promise.all([
      getContacts(options),
      getCoreContacts(options),
    ]);

    const conversationContacts = conversationResponse?.data?.data?.data || [];

    setContactConversations(conversationContacts);

    const coreContacts = coreResponse?.data?.data?.data || [];

    const filteredCoreContacts = filterOutDuplicateCoreContacts({
      coreContacts,
      conversationContacts,
    });

    setCoreContacts(filteredCoreContacts);

    return conversationResponse.data.data;
  };

  const getMessageTemplates = async () => {
    const options = {
      params: {
        pageSize: 100,
        pageNumber: 1,
      },
    };
    const response = await getTemplates(options);

    state.templates = response.data?.data;

    return response;
  };

  const setChatRoomSearchQuery = (query) => {
    state.chatRoomSearchQuery = query;
  };

  const fetchUnreadCounts = async () => {
    const response = await getUnreadCounts();

    state.unreadCounts = response?.data?.data || {};

    return response;
  };

  const reset = () => {
    Object.assign(state, getInitialState());
  };

  return {
    filter,
    pagination,

    ...toRefs(state),

    conversations,
    loadingConversations,

    connectionState,
    conversationsState,
    conversationsClient,

    getConversationBySid,
    getConversationUnreadCount,
    conversationHasUnreadMessages,
    calculateUnreadCount,
    executeCalculateUnreadCount,

    unreadConversationsCount,
    cachedUnreadResults,

    templatesDrawerState,
    toggleTemplatesDrawer,
    templateFormDrawerState,
    toggleTemplateFormDrawer,

    detachMatchedMessages,
    filterOutDuplicateCoreContacts,
    getContactConversations,
    setContactConversations,
    setCoreContacts,
    setChatRoomSearchQuery,
    getMessageTemplates,
    fetchUnreadCounts,
    reset,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useChatStore, import.meta.hot));
}
